import { faFileChartColumn, faFileCsv } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AddIcon from '@mui/icons-material/Add';
import { Button, Stack, Tooltip, Typography } from '@mui/material';
import { isEmpty } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { getAll } from '../../api/ventura/contract';
import { ADMIN_CONTRACT_NEW_URL, ADMIN_CONTRACTS_SEARCH_URL, REPORTING_HUB_URL } from '../../constants/routeUrls';
import { useConfig } from '../../hooks/useConfig';
import useContractsKpiPerformance from '../../hooks/useContractsKpiPerformance';
import useContractsPacing from '../../hooks/useContractsPacing';
import useKpiOptions from '../../hooks/useKpiOptions';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import useModal from '../../hooks/useModal';
import { formatYearFirst, replaceSpacesWithPlus } from '../../util/helpers';
import { aggregateContractKpis, KpiPerformance } from '../../util/kpiPerformance';
import BulkEdit from '../BulkEdit/BulkEditForm';
import ActiveContractFilters from './ActiveContractFilters';
import ContractColumnFilter from './ContractColumnFilter';
import ContractsFilters from './ContractFilters';
import { ContractsContext } from './ContractsContext';
import ContractSearch from './ContractSearch';
import ContractsTable, { ContractListMode } from './ContractsTable';

interface ContractsProps {
  mode?: ContractListMode;
  value?: any[];
  onChange?: Function;
}
function Contracts({ mode = ContractListMode.Default, value: selectedContracts = [], onChange }: ContractsProps) {
  const [contractData, setContractData] = useState([]);
  const [user] = useLocalStorage('user', null);
  const { id: userId, name: userName, is_account_manager: isAccountManager } = user;
  const [isInitialized, setInitialized] = useState(false);
  const [filters, setFilters] = useLocalStorage('contractsFilters', {});
  const [isLoading, setIsLoading] = useState(true);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 50,
  });
  const [paginationState, setPaginationState] = useState({ total_items: 0 });
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  // Need to sort by 'contracts.id' because id is too generic in the final join statement in the backend.
  const [orderBy, setOrderBy] = useState('contracts.id');
  const [topAdvertisersActive, setTopAdvertisersActive] = useState(false);
  const { apiHostFor } = useConfig();
  // Contracts index opts into the per-domain flip: resolves to ACH iff contracts
  // is flipped ON, else the plain Rails apiHost. The index envelope
  // ({collection,pagination}) is shape-identical across both backends for the
  // fields this grid reads (collection + pagination.total_items), so no response
  // adaptation is needed — only the host switch.
  const contractsApiHost = apiHostFor('contracts');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleTopAdvertisersToggle = useCallback(() => {
    setTopAdvertisersActive((prev) => !prev);
    setPaginationModel((prev) => ({ page: 0, pageSize: prev.pageSize }));
  }, []);

  const handleOrderByChange = (orderByColumn, direction) => {
    if (orderByColumn) {
      setOrderBy(orderByColumn);
      setOrder(direction);
    }
  };

  // logic for catching filter changes from filter components
  // reformatting the filters object to be compatible with the API
  // request format.
  const modifyFilters = useCallback(
    (filterType, value) => {
      const handleFilterChange = (filterValue) => {
        if (value) {
          setFilters({ ...filters, [filterType]: filterValue });
        } else {
          delete filters[filterType];
          setFilters({ ...filters });
        }
        setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
      };

      // when value is null, remove the filter
      if (value === null) {
        handleFilterChange(null);
      } else if (filterType === 'io_start_date' || filterType === 'io_end_date') {
        const formattedValue = {
          input_errors: {},
          range_start: formatYearFirst(value.start),
          range_end: formatYearFirst(value.end),
        };
        handleFilterChange(formattedValue);
      } else {
        handleFilterChange(value);
      }
    },
    [filters, setFilters, paginationModel.pageSize]
  );

  // Universal header search routes here as /admin/contracts?search=<name>. Apply
  // it as a name filter, then strip the param so it applies exactly once — this
  // fires whether the list mounts fresh from another tab or is already open, and
  // re-searching the same term re-triggers because the param goes empty→set again.
  const nameSearch = searchParams.get('search');
  useEffect(() => {
    if (nameSearch) {
      modifyFilters('name', { name: nameSearch });
      // Clone rather than mutate the router's URLSearchParams so the update stays
      // side-effect-free if the instance is referenced elsewhere.
      const next = new URLSearchParams(searchParams);
      next.delete('search');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameSearch]);

  // We will use isInitialized to control the race conditions for the 2 useEffects() below.
  // One is to set the default trafficker filter to be the logged in users.
  // The second one is there to catch changes in the filter and fetch the contracts.
  useEffect(() => {
    // Skip the default trafficker filter when arriving via ?search=<name>: the
    // name filter (applied in the effect above) is the intent, and defaulting to
    // the current user's trafficker would hide any contract they didn't traffic.
    if (isEmpty(filters) && userId && userName && !nameSearch) {
      modifyFilters(isAccountManager ? 'account_manager' : 'trafficker', {
        id: userId?.toString(),
        name: userName,
      });
      setInitialized(true);
    }

    // Initialize in the SAME pass as the default filter above: React batches both state
    // updates into one render, so the fetch effect below still runs exactly once, with
    // the filter applied — and it no longer waits out a 500ms timer before first paint.
    setInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Deliberately leave out dependencies as we want this run only at load up

  // update filterQuery state when filters change
  useEffect(() => {
    let compiledString = '';
    let filterQuery = '';
    const filtersArray = [];

    if (filters && Object.keys(filters).length > 0) {
      Object.keys(filters).forEach((filterType) => {
        if (filters[filterType]) {
          if (filterType === 'io_start_date' || filterType === 'io_end_date') {
            filtersArray.push(
              `{"name":"${filterType}","value":{"input_errors":{},"range_start":"${filters[filterType].range_start}","range_end":"${filters[filterType].range_end}"}}`
            );
          } else {
            filtersArray.push(`{"name":"${filterType}","value":${JSON.stringify(filters[filterType])}}`);
          }
        }
      });
      compiledString += `[${filtersArray.join(',')}]`;

      // replace whitespace characters with plus
      const replacedSpaces = replaceSpacesWithPlus(compiledString);

      filterQuery = replacedSpaces;
    } else {
      filterQuery = '';
    }

    // fetch contracts from the API
    const getContracts = async () => {
      setIsLoading(true);
      const response = await getAll(
        contractsApiHost,
        paginationModel.page,
        paginationModel.pageSize,
        filterQuery,
        orderBy,
        order,
        topAdvertisersActive
      );
      if (response.collection) {
        setContractData(response.collection);
        setPaginationState(response.pagination);
      }
      setIsLoading(false);
    };

    if (isInitialized) {
      getContracts();
    }
  }, [
    isInitialized,
    filters,
    contractsApiHost,
    orderBy,
    order,
    paginationModel.page,
    paginationModel.pageSize,
    topAdvertisersActive,
  ]);

  // ADOPS-2705: per-page KPI performance backing the KPI Performance + Risk
  // columns. Fetched by the visible contract ids and aggregated with the reused
  // kpi_options goal-format catalog into the per-contract display model.
  const contractIds = useMemo(() => contractData.map((c: any) => c.id), [contractData]);
  const { data: kpiPerformanceResponse } = useContractsKpiPerformance(contractIds);
  // ACH-bj8d: the BigQuery spend payload is off the index critical path
  // (?spend=deferred) and lazy-loaded for the visible page, then merged back onto the
  // rows — the Pacing / 5-Day columns fill in when it lands and stay empty if it fails.
  const { data: pacingResponse } = useContractsPacing(contractIds);
  const contractRows = useMemo(() => {
    const pacing = pacingResponse?.pacing ?? {};
    return contractData.map((c: any) => ({ ...c, ...(pacing[String(c.id)] ?? {}) }));
  }, [contractData, pacingResponse]);
  const { data: kpiOptions } = useKpiOptions();
  const kpiByContractId = useMemo(() => {
    const raw = kpiPerformanceResponse?.kpi_performance ?? {};
    const goalFormats = kpiOptions?.kpi_goal_formats ?? {};
    const byId: Record<string, KpiPerformance[]> = {};
    Object.keys(raw).forEach((id) => {
      byId[id] = aggregateContractKpis(raw[id], goalFormats);
    });
    return byId;
  }, [kpiPerformanceResponse, kpiOptions]);

  const [openBulkEditForm, closeBulkEditForm] = useModal(() => <BulkEdit onClose={closeBulkEditForm} />);

  // _v2: the KPI Performance + Risk columns were inserted mid-grid (ADOPS-2705),
  // shifting the numeric column indices this key stores — bump resets stale prefs once.
  const [hiddenColumns, setHiddenColumns] = useLocalStorage('contractsColumnFilters_v2', []);
  const contractContextValues = useMemo(() => {
    const columnHeadings = [
      'Contract Name',
      'Action',
      'Pacing',
      '5-Day Pacing',
      'KPI Performance',
      'Risk',
      'Status',
      'Start Date',
      'End Date',
    ];
    return {
      columnHeadings,
      hiddenColumns,
      setHiddenColumns,
      filters: filters || {},
      modifyFilters,
      topAdvertisersActive,
      toggleTopAdvertisers: handleTopAdvertisersToggle,
    };
  }, [hiddenColumns, setHiddenColumns, filters, modifyFilters, topAdvertisersActive, handleTopAdvertisersToggle]);

  return (
    <ContractsContext.Provider value={contractContextValues}>
      <Stack p={2}>
        <Stack justifyContent="space-between" direction="row" my={1}>
          <Stack direction="row" alignItems="center">
            {mode !== ContractListMode.SelectOnly && (
              <Tooltip title="Bulk Edit">
                <Button
                  size="small"
                  variant="text"
                  onClick={openBulkEditForm}
                  color="primary"
                  sx={{ minWidth: 'auto' }}
                >
                  <FontAwesomeIcon icon={faFileCsv} />
                </Button>
              </Tooltip>
            )}
            <ContractColumnFilter />
            <ContractsFilters />
            <Button
              variant={topAdvertisersActive ? 'contained' : 'outlined'}
              size="small"
              onClick={handleTopAdvertisersToggle}
            >
              Top 25 Advertisers
            </Button>
          </Stack>
          <Stack direction="row" spacing={2}>
            {/* The embedded contract picker (SelectOnly) has no universal header,
                so it keeps its own name search, wired to filter the picker's table
                via ContractsContext rather than navigating. The full-page list uses
                the search in the universal header (HeaderSearch) instead. */}
            {mode === ContractListMode.SelectOnly && (
              <ContractSearch
                host={apiHostFor('contractSearch')}
                path={ADMIN_CONTRACTS_SEARCH_URL}
                placeholder="Contract"
                onSelect={(name) => modifyFilters('name', { name })}
              />
            )}
            {mode !== ContractListMode.SelectOnly && (
              <>
                <Button
                  variant="outlined"
                  onClick={() => navigate(REPORTING_HUB_URL)}
                  startIcon={<FontAwesomeIcon icon={faFileChartColumn} />}
                  size="small"
                >
                  Report
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(ADMIN_CONTRACT_NEW_URL)}
                  size="small"
                >
                  <Typography>New Contract</Typography>
                </Button>
              </>
            )}
          </Stack>
        </Stack>
        <Stack py={1}>
          <ActiveContractFilters />
          <ContractsTable
            contractData={contractRows}
            kpiByContractId={kpiByContractId}
            rowCount={paginationState.total_items}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            isLoading={isLoading}
            mode={mode}
            value={selectedContracts}
            onChange={onChange}
            onRequestSort={handleOrderByChange}
          />
        </Stack>
      </Stack>
    </ContractsContext.Provider>
  );
}

export default Contracts;
